<template>
  <b-card>
    <b-form @submit.prevent="awaitingEmailCode ? confirmEmailChange() : submitEmailChange()">
      <b-row>
        <b-col cols="12">
          <b-card-title>Update Email Address</b-card-title>
          <b-form-group
            label="Current Email Address"
            label-for="current-email"
          >
            <b-form-input
              id="current-email"
              v-model="currentEmail"
              type="email"
              readonly
              plaintext
            />
          </b-form-group>
          <template v-if="awaitingEmailCode">
            <p
              id="email-code-description"
              class="pending-email mb-1"
            >
              A code has been sent to <strong>{{ pendingEmail }}</strong>.
              Enter it below to apply your changes.
            </p>
            <b-form-group
              label="Verification code"
              label-for="email-verification-code"
            >
              <b-form-input
                id="email-verification-code"
                v-model="verificationCode"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                required
                autofocus
                :disabled="emailLoading"
                aria-describedby="email-code-description email-change-error"
                placeholder="Enter verification code"
                @input="emailError = ''"
              />
            </b-form-group>
          </template>
          <template v-else>
            <template v-if="allowedEmailSuffixes.length">
              <p
                id="email-suffix-description"
                class="mb-50"
              >
                Your email address must end in one of the following addresses associated with your company:
              </p>
              <ul class="email-suffixes mb-1">
                <li
                  v-for="suffix in allowedEmailSuffixes"
                  :key="suffix"
                >
                  @{{ suffix }}
                </li>
              </ul>
            </template>
            <p
              v-else
              id="email-suffix-description"
              class="mb-1"
            >
              {{ emailChangeUnavailableReason }}
            </p>
            <b-form-group
              label="New Email Address"
              label-for="email-input"
            >
              <b-form-input
                id="email-input"
                v-model="newEmail"
                type="email"
                required
                maxlength="254"
                :disabled="emailLoading || !allowedEmailSuffixes.length"
                :state="emailValidationMessage ? false : null"
                aria-describedby="email-suffix-description email-validation-error email-change-error"
                placeholder=""
                @input="emailError = ''"
              />
              <b-form-invalid-feedback id="email-validation-error">
                {{ emailValidationMessage }}
              </b-form-invalid-feedback>
            </b-form-group>
          </template>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-1"
            type="submit"
            :disabled="awaitingEmailCode ? !canConfirmEmailChange : !canSubmitEmailChange"
          >
            <b-spinner
              v-if="emailLoading"
              small
            />
            <span v-else>{{ awaitingEmailCode ? 'Apply changes' : 'Send verification code' }}</span>
          </b-button>
          <p
            v-if="emailError"
            id="email-change-error"
            class="text-danger mt-1 mb-0"
            role="alert"
          >
            {{ emailError }}
          </p>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BButton, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BRow, BCol, BCard, BCardTitle, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    generalData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      emailLoading: false,
      emailError: '',
      newEmail: this.generalData.pendingEmailExpired ? this.generalData.pendingEmail || '' : '',
      verificationCode: '',
      currentEmail: this.generalData.email || '',
      pendingEmail: this.generalData.pendingEmail || '',
      pendingEmailExpired: this.generalData.pendingEmailExpired || false,
    }
  },
  computed: {
    awaitingEmailCode() {
      return Boolean(this.pendingEmail) && !this.pendingEmailExpired
    },
    canConfirmEmailChange() {
      return this.awaitingEmailCode && !this.emailLoading && /^[0-9]{6}$/.test(this.verificationCode.trim())
    },
    allowedEmailSuffixes() {
      return this.generalData.allowedEmailSuffixes || []
    },
    emailChangeUnavailableReason() {
      return this.generalData.emailChangeUnavailableReason || 'Your company has no approved email addresses. Please contact your company administrator or support.'
    },
    emailValidationMessage() {
      const email = this.newEmail.trim()
      if (!email) return ''
      if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return 'Please enter a valid email address.'
      }
      if (!this.allowedEmailSuffixes.includes(email.split('@')[1].toLowerCase())) {
        return 'Please use one of the email addresses associated with your company.'
      }
      if (email.toLowerCase() === this.currentEmail.trim().toLowerCase()) {
        return 'Please enter a different email address.'
      }
      return ''
    },
    canSubmitEmailChange() {
      return !this.awaitingEmailCode && !this.emailLoading && this.allowedEmailSuffixes.length > 0
        && this.newEmail.trim().length > 0 && !this.emailValidationMessage
    },
  },
  watch: {
    generalData: {
      handler(newVal) {
        this.currentEmail = newVal.email || ''
        this.pendingEmail = newVal.pendingEmail || ''
        this.pendingEmailExpired = newVal.pendingEmailExpired || false
        this.verificationCode = ''
        this.newEmail = this.pendingEmailExpired ? this.pendingEmail : ''
      },
    },
    'generalData.email': {
      handler(email) {
        this.currentEmail = email || ''
      },
    },
  },
  methods: {
    submitEmailChange() {
      if (!this.canSubmitEmailChange) return
      this.emailError = ''
      this.emailLoading = true
      this.$http
        .post('account.cfc?method=emailchange', new URLSearchParams({ emailname: this.newEmail.trim() }))
        .then(response => {
          this.emailLoading = false

          if (response.data.success) {
            this.pendingEmail = response.data.pendingEmail || ''
            this.pendingEmailExpired = response.data.pendingEmailExpired || false
            this.$emit('email-change-pending', {
              pendingEmail: this.pendingEmail,
              pendingEmailExpired: this.pendingEmailExpired,
            })
            this.newEmail = ''
            this.verificationCode = ''
          } else {
            this.showEmailError(response.data.error || 'Your request could not be completed. Please try again.')
          }
        })
        .catch(() => {
          this.emailLoading = false
          this.showEmailError('Your request could not be confirmed. Please refresh the page to check its status before trying again.')
        })
    },
    confirmEmailChange() {
      if (!this.canConfirmEmailChange) return
      this.emailError = ''
      this.emailLoading = true
      this.$http
        .post('account.cfc?method=confirmUserEmailChange', new URLSearchParams({
          verificationCode: this.verificationCode.trim(),
          emailname: this.pendingEmail,
        }))
        .then(response => {
          if (response.data.success) {
            this.currentEmail = response.data.email
            this.pendingEmail = ''
            this.pendingEmailExpired = false
            this.verificationCode = ''
            this.newEmail = ''
            this.$emit('email-changed', {
              email: this.currentEmail,
              pendingEmail: '',
              pendingEmailExpired: false,
            })
            this.$bvToast.toast('Your email address has been changed successfully.', {
              title: 'Success',
              variant: 'success',
              solid: true,
              autoHideDelay: 4000,
            })
          } else {
            if (response.data.codeExpired) {
              this.pendingEmailExpired = true
              this.newEmail = this.pendingEmail
              this.verificationCode = ''
              this.$emit('email-change-pending', {
                pendingEmail: this.pendingEmail,
                pendingEmailExpired: true,
              })
            }
            this.showEmailError(response.data.error || 'Your email address could not be changed. Please try again.')
          }
        })
        .catch(() => {
          this.showEmailError('Your email change could not be confirmed. Please refresh the page to check its status before trying again.')
        })
        .finally(() => { this.emailLoading = false })
    },
    showEmailError(message) {
      this.emailError = message
      this.$bvToast.toast(message, {
        title: 'Error',
        variant: 'danger',
        solid: true,
        autoHideDelay: 4000,
      })
    },
  },
}
</script>

<style scoped>
.email-suffixes,
.pending-email {
  overflow-wrap: anywhere;
}
</style>
